import { GitBookUsers, type OrganizationMember } from '@gitbook/api-client';
import { createPalette, type PaletteCommandDef, PaletteCommandType } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';

import { apiResource, readAPICache } from '../../state';
import { UserAvatar } from '../Users';

/**
 * Helper to wrap searching for users across the app by using the
 * listMembersInOrganizationById API endpoint.
 */
export function searchOrganizationMembers({
    organizationId,
    query,
    limit,
}: {
    organizationId: string;
    query: string;
    limit?: number;
}): OrganizationMember[] {
    const organization = readAPICache(
        apiResource('getOrganizationById', {
            organizationId,
        })
    );

    if (!organization.permissions.listMembers) {
        return [];
    }

    const response = readAPICache(
        apiResource('listMembersInOrganizationById', {
            organizationId,
            search: query,
            limit,
        })
    );

    if (!response) {
        return [];
    }

    return response.items;
}

/**
 * Create a palette to pick an organization member.
 */
export function createPickOrganizationMemberPalette({
    organizationId,
    selectedUserId,
    onPick,
    suggestGitBookAgent = false,
}: {
    organizationId: string;

    /**
     * The user ID of the selected user.
     */
    selectedUserId?: string;

    /**
     * Callback when a user is selected.
     */
    onPick: (userId?: string) => void;

    /**
     * If true, GitBook Agent will be suggested as a user.
     */
    suggestGitBookAgent?: boolean;
}) {
    return createPalette({
        search: { placeholder: 'Filter by teammate' },
        groups: [
            {
                key: 'members',
                commands: (query) => {
                    const members = searchOrganizationMembers({
                        organizationId,
                        query,
                    });
                    return members.map((member) => {
                        const command: PaletteCommandDef = {
                            type: PaletteCommandType.RADIO,
                            key: member.user.id,
                            title: member.user.displayName,
                            icon: <UserAvatar user={member.user} size="xs" />,
                            selected: selectedUserId === member.user.id,
                            trigger: () => {
                                onPick(member.user.id);
                            },
                        };

                        return command;
                    });
                },
            },
            ...(suggestGitBookAgent
                ? [
                      {
                          key: 'gitbook-agent',
                          commands: [
                              {
                                  type: PaletteCommandType.RADIO,
                                  key: GitBookUsers.GitbookAgent,
                                  title: 'GitBook Agent',
                                  icon: Icon.AIAgentAnimated,
                                  selected: selectedUserId === GitBookUsers.GitbookAgent,
                                  trigger: () => {
                                      onPick(GitBookUsers.GitbookAgent);
                                  },
                              } as PaletteCommandDef,
                          ],
                      },
                  ]
                : []),
        ],
    });
}
